import { MILISECONDS_IN_DAY, UNKNOWN_DATUM } from 'business/constants';
import type { ChartSettingsT, VizType } from 'business/definitions';
import {
    canCumulateDataTypes,
    DataTypes as DT,
    SlidingTimeScales as STS,
    TimeScales,
    TimeScales as TS,
} from 'business/definitions';
import {
    formatData,
    formatDisplay,
    formatSlidingWeek,
    formatSlidingYear,
} from 'helpers/formatHelper';
import {
    capitalizeAndKeepTheRest,
    isDate,
    isTypeNumeric,
    orderColumns,
} from 'helpers/helpers';
import { camelize } from 'helpers/syntaxHelper';
import { translateBlock } from 'i18n';
import uniq from 'lodash/uniq';
import type { SerieTooltipData } from 'shared/components/ChartContainer/components/Chart/graphs/ChartOption/types';
import type {
    EffectiveRequestFilter,
    GetDataMakerResponse,
} from 'shared/components/ChartContainer/types';

import {
    getQuarterNumber,
    getSemesterNumber,
    MONTHS,
    QUARTERS,
    SEMESTERS,
} from './dateHelper';
import { canCumulateVizTypes } from './vizInfos';

/**
 * Format the data returned by a query for D3
 * @param  {Array} data    data returned by the get_data endpoint
 * @param  {Array} columns columns returned by the get_data endpoint
 * @param  {Array} extraColumns additional columns to include in the formatted data, these columns are not formatted
 *                            but simply copied from the original data (phases for example)
 * @return {formattedData}         formatted data ready to be used by D3
 */
export const cleanChartData = (
    data: ResponseDataT[],
    columns: ResponseColumnT[],
    extraColumns?: string[],
): ResponseDataT[] => {
    const formattedData: ResponseDataT[] = data.map((d: ResponseDataT) => {
        const newD = {};
        columns.forEach((col) => {
            const formattedValue = formatData(d[col.key], col.type);
            if (
                (col.type === 'integer' || col.type === 'float') &&
                Number.isNaN(formattedValue)
            ) {
                // @ts-expect-error [TS migration] Was not detected by flow
                newD[col.key] = UNKNOWN_DATUM;
            } else {
                // @ts-expect-error [TS migration] Was not detected by flow
                newD[col.key] = formattedValue;
            }
        });

        if (extraColumns) {
            extraColumns.forEach((col) => {
                // @ts-expect-error [TS migration] Was not detected by flow
                newD[col] = d[col];
            });
        }

        return newD;
    });
    return formattedData;
};

function isDiscrete(type: DataTypesT): boolean {
    return [
        DT.category,
        DT.integer,
        DT.text,
        DT.year,
        DT.enum,
        DT.tree,
    ].includes(type);
}

export function isContinuous(type: DataTypesT): boolean {
    return !isDiscrete(type);
}

/**
 * Returns true for types that go through the interpolated-bins path
 * (integer, year, continuous). Used to decide whether an x-axis needs
 * an explicit interval and bin computation.
 */
export function needsInterpolatedBins(type: DataTypesT): boolean {
    return type === DT.integer || type === DT.year || isContinuous(type);
}

/**
 * Returns true for date/datetime columns that already have a time interval
 * derived from their scale (via getTimeInterval). Calling getInterval on these
 * axes would trigger a warning since it should not happen.
 */
export function hasExplicitTimeScale(
    type: DataTypesT,
    scale: TimeScalesT | null | undefined,
): boolean {
    return !!scale && (type === DT.date || type === DT.datetime);
}

export function isDateOrTime(type: DataTypesT): boolean {
    return isDate(type) || type === 'time';
}

/**
 * Returns true when the x axis holds numeric values for which we computed an
 * interval (raw floats, or histogram bins whose value is the bin midpoint).
 * Such an axis is rendered as a category axis without explicit `axis.data`
 * (@see BarChartOption.getXAxisType).

 */
export function isNumericIntervalAxis(
    type: DataTypesT,
    interval: number | null | undefined,
): boolean {
    return isTypeNumeric(type) && Boolean(interval);
}

/** Viz types whose continuous x values are grouped client-side into bins.
 * @see formatDataToHistogramWithContext
 */
const HISTOGRAM_VIZ_TYPES: VizType[] = [
    'bar',
    'horizontal_bar',
    'pyramid',
    'polar_bar',
];

/**
 * Returns true when the x values are grouped into histogram bins, which turns
 * the x axis into a category axis whose order can be changed, unlike a true
 * value axis.
 */
export function isHistogramXAxis(
    vizType: VizType,
    xType: DataTypesT | undefined,
): boolean {
    return (
        !!xType &&
        HISTOGRAM_VIZ_TYPES.includes(vizType) &&
        isContinuous(xType) &&
        !isDateOrTime(xType)
    );
}

/**
 * Get the React Query cache key for a chart data query.
 */
export const getQueryString = (query: QueryT): string =>
    JSON.stringify({
        metrics: query.metrics,
        dimensions: query.dimensions,
        filterExpression: query.filterExpression ?? null,
        dashboardFilters: query.dashboardFilters ?? [],
        detailsFilters: query.detailsFilters ?? [],
    });

export const translateTreeLabel = (
    title: string,
    treeValueTranslations?: { [key: string]: string } | null,
): string | null => {
    if (title === undefined) return null;
    if (title === UNKNOWN_DATUM) return '-';
    return treeValueTranslations
        ? treeValueTranslations[title.toString()]
        : null;
};

/**
 * Formats a date value based on the provided time scale.
 *
 * @param value - The date value to format.
 * @param type - The type of data.
 * @param scale - The time scale to use for formatting.
 * @param interval - interval for range-based formatting, used for week scale.
 * @param isAxis - Whether the function is used for an axis label.
 * @returns - The formatted date string.
 */
const formatDateByScale = (
    value: string | number,
    type: DataTypesT,
    scale: TimeScalesT,
    interval?: number | null,
    isAxis: boolean = false,
): string => {
    const date = new Date(parseInt(value.toString(), 10));
    const year = date.getFullYear();

    switch (scale) {
        case TS.week: {
            if (typeof value !== 'number' || !interval)
                return formatDisplay(value, type);
            const startDate = formatDisplay(value, type);
            const endDate = formatDisplay(value + interval, type);
            return `${startDate} - ${endDate}`;
        }
        case TS.month:
            return `${MONTHS[date.getMonth()]} ${year}`;
        case TS.year:
            // we only want to display the year once per year - not once per tick !
            if (isAxis && date.getMonth() !== 0) return '';
            return `${year}`;
        case TS.quarter:
            return `${QUARTERS[getQuarterNumber(date.getMonth())]} ${year}`;
        case TS.semester:
            return `${SEMESTERS[getSemesterNumber(date.getMonth())]} ${year}`;
        case TS.schoolYear:
            return `${year} - ${year + 1}`;
        case STS.slidingWeek:
            return formatSlidingWeek(date);
        case STS.slidingYear:
            return formatSlidingYear(date);
        default:
            return formatDisplay(value, type);
    }
};

/**
 * Format the label for a numeric axis with intervals
 */
const getNumericIntervalLabel = (
    value: number,
    interval: number,
    type: DataTypesT,
): string => {
    const intervalLowerBound = formatDisplay(value - interval / 2, type);
    const intervalUpperBound = formatDisplay(value + interval / 2, type);
    return `${intervalLowerBound} - ${intervalUpperBound}`;
};

/**
 * Format label of an eChart axis
 */
export function formatAxisLabel(
    value: string | number | undefined,
    type: DataTypesT,
    decimalPrecision: 0 | 1 | 2,
    displayBy: ChartSettingsT['displayBy'],
    interval?: number | null,
    scale?: TimeScalesT | null,
    areTicksRotated?: boolean | null,
    treeValueTranslations?: { [key: string]: string } | null,
    isPyramid: boolean = false,
    shouldIgnoreEllipsis: boolean = false,
): string | number {
    if (value === undefined) return '';
    const numericValue = typeof value === 'number' ? value : parseFloat(value);
    if (isPyramid) {
        // We want to display the full value interval for the pyramid chart
        // e.g. : we want to display 20-24 instead of 22.5
        // N.B. : this will need to be update if the bin size is updated
        return `${numericValue - 2.5} - ${numericValue + 1.5}`;
    }
    if (isDate(type) && scale) {
        return formatDateByScale(value, type, scale, interval, true);
    }
    if (isTypeNumeric(type) && interval) {
        return getNumericIntervalLabel(numericValue, interval, type);
    }
    let label = null;
    if (type === DT.tree) {
        label = translateTreeLabel(value.toString(), treeValueTranslations);
    } else {
        label = formatDisplay(value, type, decimalPrecision, displayBy);
    }

    const labelToDisplay = label;
    if (labelToDisplay && labelToDisplay.length > 15 && !shouldIgnoreEllipsis) {
        // We only display the first 15 chars for readability
        if (areTicksRotated) return `${labelToDisplay.substring(0, 15)}...`;
    }
    return capitalizeAndKeepTheRest(labelToDisplay);
}

/**
 * Format tooltip title of a chart, useful for histogram and date ranges.
 */
export function formatTooltipTitle(
    value: string | number,
    type: DataTypesT,
    scale?: TimeScalesT | null,
    interval?: number | null,
    treeValueTranslations?: { [key: string]: string } | null,
): string | number {
    const numericValue = typeof value === 'number' ? value : parseFloat(value);
    if (isDate(type) && scale && interval) {
        return formatDateByScale(value, type, scale, interval);
    } else if ([DT.float, DT.money].includes(type) && interval) {
        // Used for histograms
        return getNumericIntervalLabel(numericValue, interval, type);
    }
    const formatedTitle = formatDisplay(value, type, 0);
    const translatedTitle = translateTreeLabel(
        value.toString(),
        treeValueTranslations,
    );
    return translatedTitle || formatedTitle;
}

/**
 * Build a full tooltip string for an eChart graph.
 * Tooltips cannot be HTML with eCharts, so we simply build
 * a string containing all the required elements.
 */
export function getTooltipString(
    title: string,
    series: Array<SerieTooltipData>,
) {
    const seriesString = series.map((serie) =>
        serie.value != null
            ? `<div class="tooltip-serie">${serie.marker}${capitalizeAndKeepTheRest(serie.label)} : ${serie.value}</div>`
            : null,
    );
    return `
        <div>${capitalizeAndKeepTheRest(title)} ${seriesString.join(' ')}</div>
    `;
}

export function getAxisType(type: DataTypesT): 'category' | 'time' | 'value' {
    if (isDiscrete(type)) {
        return 'category';
    }
    if (isDate(type) || type === DT.time) {
        return 'time';
    }
    return 'value';
}

export function getTimeInterval(
    scale?: TimeScalesT | null,
    date: Date | null = null,
): number {
    // FIXME: should be dead code - remove me
    switch (scale) {
        case 'day':
            return MILISECONDS_IN_DAY;
        case 'week':
            return MILISECONDS_IN_DAY * 7;
        case 'month': {
            const days = date
                ? new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
                : 30;
            return MILISECONDS_IN_DAY * days;
        }
        case 'quarter':
            return MILISECONDS_IN_DAY * 31 * 3;
        case 'semester':
            return MILISECONDS_IN_DAY * 31 * 6;
        case 'year':
            return MILISECONDS_IN_DAY * 365;
        case 'school_year':
            return MILISECONDS_IN_DAY * 365;
        case 'sliding_year':
            return MILISECONDS_IN_DAY * 365;
        case 'sliding_week':
            return MILISECONDS_IN_DAY * 7;
        default:
            return 0;
    }
}

export function isEmptyChart(chartData: ResponseDataT[]): boolean {
    const onlyUnknownData = chartData.every((d) => {
        const valueSet = uniq(Object.values(d));
        return valueSet.length === 1 && valueSet[0] === UNKNOWN_DATUM;
    });
    return chartData.length === 0 || onlyUnknownData;
}

export function canCumulate(
    dimensions: ReadonlyArray<(QueryDimensionT & WithType) | ResponseColumnT>,
    vizType: VizType,
): boolean {
    return (
        dimensions.filter(
            (d) =>
                d.type &&
                canCumulateDataTypes.includes(d.type) &&
                d.scale !== TimeScales.dow,
        ).length === 1 && canCumulateVizTypes.includes(vizType)
    );
}

// TODO This function is a light version of the setQueryResult we should refactor it with this last.
export function cleanQueryResult(payload: {
    queryMetrics: QueryMetricT[];
    queryDimensions: QueryDimensionT[];
    columns: ResponseColumnT[];
    data: ResponseDataT[];
    trees?: ResponseTreesT;
    timeFilters?: EffectiveRequestFilter[];
    alertTriggered?: boolean;
    extraColumns?: string[];
}): GetDataMakerResponse {
    const {
        queryMetrics,
        queryDimensions,
        columns,
        data,
        trees,
        extraColumns,
    } = payload;

    const orderedColumns = orderColumns(columns, queryDimensions);

    // object.key is camelized, object.field is snake cased

    const queryResponseColumns = orderedColumns.map((col) => {
        let colType = col.type;
        let isMantyId = false;
        if (colType === 'table') {
            const colMetric = queryMetrics.find(
                (m) => camelize(m.field) === col.key,
            );
            if (colMetric && colMetric.aggregation === 'number') {
                colType = 'integer';
            } else {
                colType = 'text';
                // If the aggregation is not a number and the column is a table,
                // It is the manty id column. We'll use this boolean to hide it in the chart.
                isMantyId = true;
            }
        }
        return {
            ...col,
            key: col.key,
            label: translateBlock(col.key),
            type: colType,
            isMantyId: isMantyId,
        };
    });

    const queryResponseData = cleanChartData(
        data,
        queryResponseColumns,
        extraColumns,
    );
    return {
        columns: queryResponseColumns,
        data: queryResponseData,
        trees: trees,
        timeFilters: payload.timeFilters,
        alertTriggered: payload.alertTriggered,
    };
}
